import { useEffect, useMemo, useState } from 'react';
import { Page } from './Page';
import { Icon, SplitRow, FilterControl, type FilterOption } from '../components/common';
import { AccountChartCards } from '../components/account/AccountChartCards';
import { useAccountModal } from '../components/account/AccountModalProvider';
import { AccountView } from '../components/account/AccountView';
import { ActivityFeedView } from '../components/account/ActivityFeedView';
import { PortalSettingsView } from '../components/account/PortalSettingsView';
import { api } from '../lib/api';
import { useResource } from '../lib/useResource';
import type { ActivityWindow } from '@bbbk/schema';
import type { Person, RoleFilter, User } from '../components/account/types';

// The activity surfaces show the last 30 days. Fixed (no selector) — the window
// is a deliberate default, not a knob; 30 days reads the team's recent pattern
// without the all-time scan.
const WINDOW: ActivityWindow = 'month';

// Account (admin-only). The page chrome is three chart cards (page views by
// section, sign-ins over time, engagement) then the shared search/role toolbar —
// all stable above two tabs: List (manage access + usage columns) and Feed (the
// action/sign-in feed). The page owns the toolbar's (q, filter); both tabs read
// it through one personMatches rule, and clicking a user's avatar anywhere opens
// the global edit-user modal.
//
// ONE allowlist fetch feeds the cards and both tabs (useResource — the app's SWR
// engine): the List, the Feed's actor join, and the engagement donut all read
// the same `users`, and a grant/revoke silently revalidates it via the mutation
// bus, so a change shows everywhere without a reload.
// The third tab is portal-foundations AC15's one screen. Its label is the
// prototype's (Account ▸ Customer portal, screen s4); what it contains is
// R-SET-3's Size limits block and nothing else, because nothing reads the rest
// of R-SET-1's keys until `availability-and-request`.
type Tab = 'feed' | 'list' | 'portal';
const TAB_LABEL: Record<Tab, string> = { feed: 'Feed', list: 'Users', portal: 'Customer portal' };
// The role filter options ('all' = deselected). Shared by both tabs.
// The role icons the app already uses for these roles (mirrors the stat strip).
const ROLE_OPTIONS: FilterOption[] = [
  { key: 'admin', label: 'Admins', icon: 'check' },
  { key: 'owner', label: 'Owners', icon: 'home' },
  { key: 'helper', label: 'Helpers', icon: 'paw' },
  { key: 'staff', label: 'Staff', icon: 'clock' },
  // Customers reach this list since household-app-access. The chip is here so
  // the desk can find them; the PICKER never offers the role (R-ACC-3).
  { key: 'customer', label: 'Customers', icon: 'user' },
];

export function AccountPage() {
  const [tab, setTab] = useState<Tab>('feed');
  // The add/edit user modal is global (AccountModalProvider) — the same one a
  // user's avatar opens anywhere. The header button just asks it to open.
  const modal = useAccountModal();

  // The shared role filter lives HERE — above the tabs, so it's identical on
  // every tab and never remounts as you switch. Per-page search is gone (AC3);
  // the views take an empty query and filter by role only. Each tab reads
  // (q, filter) and applies the same personMatches rule.
  const [filter, setFilter] = useState<RoleFilter>('all');

  // Keep-alive tabs: a tab mounts on first visit and STAYS mounted (hidden when
  // inactive), so switching is instant — no remount, no skeleton flash, scroll
  // and filters preserved — and a hidden tab still revalidates on a relevant
  // write (its useResource is live). The active tab is always rendered, even on
  // its very first frame.
  const [visited, setVisited] = useState<Set<Tab>>(() => new Set<Tab>(['feed']));
  useEffect(() => {
    setVisited((v) => (v.has(tab) ? v : new Set(v).add(tab)));
  }, [tab]);
  const mounted = (t: Tab) => t === tab || visited.has(t);

  // The allowlist changes only on account writes (grant/revoke/edit) — so it
  // revalidates on the 'account' tag, not every write app-wide.
  const usersRes = useResource('account:users', () => api<User[]>('/account/users').then((r) => r.data), {
    invalidatedBy: ['account'],
  });
  const users = usersRes.state.data;
  const usersError = usersRes.state.phase === 'error';
  // Stable reference unless the user list changes — so the Feed memo (peopleByKey)
  // doesn't recompute and the children don't re-render on every parent render.
  const people = useMemo<Person[]>(() => (users ?? []).map((u) => ({ email: u.email, role: u.role })), [users]);

  return (
    <Page title="Account">
      {/* One padded flex-column view owns all the vertical rhythm — chart cards,
          then the Split-Row tabs, then the shared filter toolbar, then the active
          tab body — so every element aligns to the view edges and the gaps match
          the other pages by construction. Each tab's body stays mounted and is
          shown/hidden, so the chrome never jumps and content loads in place. */}
      <div className="account-view">
        <AccountChartCards window={WINDOW} users={users} />
        <SplitRow
          tabs={(['feed', 'list', 'portal'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={'sr-tab' + (tab === t ? ' on' : '')}
              onClick={() => setTab(t)}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
          right={
            // Role filter — shared across both tabs (personMatches). "New user"
            // moved to the global top-bar New menu (admins only).
            <FilterControl
              label="Role"
              icon="user"
              mode="single"
              options={ROLE_OPTIONS}
              selected={filter === 'all' ? [] : [filter]}
              onChange={(keys) => setFilter((keys[0] as RoleFilter) ?? 'all')}
            />
          }
        />
        {mounted('list') && (
          <div className="account-tab" hidden={tab !== 'list'}>
            <AccountView window={WINDOW} q="" filter={filter} users={users} usersError={usersError} />
          </div>
        )}
        {mounted('feed') && (
          <div className="account-tab" hidden={tab !== 'feed'}>
            <ActivityFeedView window={WINDOW} q="" filter={filter} people={people} />
          </div>
        )}
        {mounted('portal') && (
          <div className="account-tab" hidden={tab !== 'portal'}>
            <PortalSettingsView />
          </div>
        )}
      </div>
    </Page>
  );
}
